/**
 * Supplier emails to orders@ that the app could not place (ADR 0311).
 *
 * The exception list, and deliberately nothing more. A supplier's
 * acknowledgement, invoice or credit memo that names one order lands on
 * that order and on the job's Docs card by itself, within minutes. What
 * reaches this screen is the email that named no order, two orders, an
 * order that does not exist, or nothing the app could tell apart — kept,
 * with its files, for a person to place or set aside.
 *
 * Nothing is chosen by guessing: the order is picked from a list, and the
 * kind is what the person says it is. An email that was never a supplier's
 * document is set aside with a reason, or deleted outright (ADR 0316).
 */

import {
  holdsRole,
  SUPPLIER_MAIL_KIND_LABEL,
  SUPPLIER_MAIL_KIND_ORDER,
  type SupplierMailKind,
} from "@roof-hawk/core";
import { useCallback, useEffect, useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t, tn } from "../../i18n";
import { fmtDateTime } from "../../lib/format";
import {
  checkOrdersInbox,
  deleteSupplierMail,
  fetchOrdersForSupplierMail,
  fetchSupplierMailForReview,
  fileSupplierMail,
  ignoreSupplierMail,
  orderChoiceLabel,
  signSupplierMailFile,
  supplierMailIsOnJob,
  type OrderForSupplierMail,
  type SupplierMailFileRow,
  type SupplierMailRow,
} from "../../lib/queries";
import { useSession } from "../../session";

const whatReachesThisList = () =>
  t(
    "Only email sent to orders@ that Roof Board could not place by itself: no MO number anywhere, more than one, a number that is not an order, or a document it could not tell was an acknowledgement, an invoice or a credit memo. An email naming one order is already on that order and on the job's Docs card.",
  );

export function SupplierMailTool() {
  const { profileId, profile } = useSession();
  const [mail, setMail] = useState<SupplierMailRow[]>([]);
  const [orders, setOrders] = useState<OrderForSupplierMail[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [chosenOrder, setChosenOrder] = useState<Record<string, string>>({});
  const [chosenKind, setChosenKind] = useState<
    Record<string, SupplierMailKind>
  >({});

  const refresh = useCallback(async () => {
    const [rows, orderRows] = await Promise.all([
      fetchSupplierMailForReview(),
      fetchOrdersForSupplierMail(),
    ]);
    setMail(rows);
    setOrders(orderRows);
  }, []);

  useEffect(() => {
    void refresh().catch((e) => setError(String(e.message ?? e)));
  }, [refresh]);

  async function run(work: () => Promise<void>) {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      await work();
      await refresh();
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setBusy(false);
    }
  }

  async function open(file: SupplierMailFileRow) {
    window.open(
      await signSupplierMailFile(file),
      "_blank",
      "noopener,noreferrer",
    );
  }

  /** The kind the person picked, or what the app read it as. */
  const kindFor = (row: SupplierMailRow): SupplierMailKind =>
    chosenKind[row.id] ?? row.kind;

  const checkNow = holdsRole(profile?.roles, "admin") ? (
    <button
      disabled={busy}
      onClick={() =>
        void run(async () => {
          const result = await checkOrdersInbox();
          const head = tn(
            result.read,
            "Read 1 new email: {filed} filed, {review} waiting here",
            "Read {n} new emails: {filed} filed, {review} waiting here",
            { filed: result.filed, review: result.review },
          );
          const tail =
            result.notified > 0
              ? " " + t("({n} emailed about).", { n: result.notified })
              : ".";
          setNote(
            head +
              tail +
              (result.errors.length ? ` ${result.errors.join(" ")}` : ""),
          );
        })
      }
    >
      {t("Check orders@ now")}
    </button>
  ) : null;

  if (mail.length === 0) {
    return (
      <div className="block">
        <h3>{t("Supplier mail")}</h3>
        <div className="muted">
          {t(
            "Nothing waiting. An acknowledgement, invoice or credit memo emailed to orders@ is filed on its order and on the job’s Docs card by itself — only ones Roof Board could not place turn up here.",
          )}
        </div>
        <div className="muted">{whatReachesThisList()}</div>
        {checkNow}
        {note && <div className="muted">{note}</div>}
        <ErrorNote error={error} />
      </div>
    );
  }

  return (
    <div className="block">
      <h3>{t("Supplier mail that needs placing")}</h3>
      <div className="muted">
        {t(
          "These came to orders@ and Roof Board could not tell which order they belong to. Pick the order and say what the document is, set the email aside if it was never a supplier document, or delete it.",
        )}
      </div>
      <div className="muted">{whatReachesThisList()}</div>
      {checkNow}
      <ErrorNote error={error} />
      {note && <div className="muted">{note}</div>}

      <table className="grid">
        <thead>
          <tr>
            <th>{t("Sent")}</th>
            <th>{t("From")}</th>
            <th>{t("Email")}</th>
            <th>{t("Files")}</th>
            <th>{t("What it is")}</th>
            <th>{t("Order")}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {mail.map((row) => (
            <tr key={row.id}>
              <td>{fmtDateTime(row.sent_at)}</td>
              <td>
                {row.from_name ?? row.from_address}
                {row.from_name && (
                  <div className="muted">{row.from_address}</div>
                )}
              </td>
              <td>
                {row.subject ?? t("(no subject)")}
                {(row.review_reasons ?? []).length > 0 && (
                  <div className="muted">
                    {(row.review_reasons ?? []).join(" ")}
                  </div>
                )}
              </td>
              <td>
                {row.files.map((file) => (
                  <div key={file.id}>
                    <button
                      className="linkish"
                      disabled={busy}
                      onClick={() => void run(() => open(file))}
                    >
                      {file.is_body ? t("The email itself") : file.filename}
                    </button>
                    {(file.ship_to_name || file.ship_to_address) && (
                      <div className="muted">
                        {t("Ship To:")}{" "}
                        {[file.ship_to_name, file.ship_to_address]
                          .filter(Boolean)
                          .join(", ")
                          .replace(/\s*\n\s*/g, ", ")}
                      </div>
                    )}
                  </div>
                ))}
              </td>
              <td>
                <select
                  aria-label={t("What {subject} is", {
                    subject: row.subject ?? t("this email"),
                  })}
                  value={kindFor(row)}
                  onChange={(e) =>
                    setChosenKind((c) => ({
                      ...c,
                      [row.id]: e.target.value as SupplierMailKind,
                    }))
                  }
                >
                  {SUPPLIER_MAIL_KIND_ORDER.map((kind) => (
                    <option key={kind} value={kind}>
                      {t(SUPPLIER_MAIL_KIND_LABEL[kind])}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <select
                  aria-label={t("Order for {subject}", {
                    subject: row.subject ?? t("this email"),
                  })}
                  value={chosenOrder[row.id] ?? row.material_order_id ?? ""}
                  onChange={(e) =>
                    setChosenOrder((c) => ({ ...c, [row.id]: e.target.value }))
                  }
                >
                  <option value="">{t("Pick an order…")}</option>
                  {orders.map((order) => (
                    <option key={order.id} value={order.id}>
                      {orderChoiceLabel(order)}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <button
                  disabled={
                    busy ||
                    !(chosenOrder[row.id] ?? row.material_order_id) ||
                    kindFor(row) === "unknown"
                  }
                  onClick={() =>
                    void run(async () => {
                      const orderId =
                        chosenOrder[row.id] ?? row.material_order_id ?? "";
                      const order = orders.find((o) => o.id === orderId);
                      if (!order) throw new Error(t("Pick an order first."));
                      await fileSupplierMail({
                        mail: row,
                        order,
                        kind: kindFor(row),
                        profileId: profileId ?? null,
                      });
                      setNote(
                        t("Filed on MO-{number} and on the job's Docs card.", {
                          number: order.order_number,
                        }),
                      );
                    })
                  }
                >
                  {t("File on this order")}
                </button>
                <button
                  disabled={busy}
                  onClick={() =>
                    void run(async () => {
                      const why =
                        window.prompt(
                          t(
                            "Set this email aside. What was it, if not a supplier document?",
                          ),
                        ) ?? "";
                      await ignoreSupplierMail(row.id, why, profileId ?? null);
                    })
                  }
                >
                  {t("Not a supplier document")}
                </button>
                {supplierMailIsOnJob(row) ? (
                  <div className="muted">
                    {t("Already on the job’s Docs card; delete it there.")}
                  </div>
                ) : (
                  <button
                    disabled={busy}
                    onClick={() =>
                      void run(async () => {
                        if (
                          !window.confirm(
                            t(
                              "Delete this email from Roof Board? It and its files are gone for good here, and it will not come back when orders@ is read again. The email itself stays in Gmail.",
                            ),
                          )
                        )
                          return;
                        await deleteSupplierMail(row, profileId ?? null);
                        setNote(t("Deleted."));
                      })
                    }
                  >
                    {t("Delete")}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
